import { useCurrentUser } from '@/app/providers/user';
import { useLogoutRedirect } from '@/features/auth/hooks/useLogoutRedirect';
import { useWallet } from '@/app/providers/wallet';
import { formatCredits, initialsOf } from '@/shared/utils/format';
import { balanceLevel } from '@/features/wallet/utils/walletBalance';
import { Button, ButtonLink, Card, CardHeader, Pill } from '@/shared/ui';

/*
 * Not in Iteration 2.0 — inferred, because deleting the top nav left the user
 * chip, the credit balance and sign-out with nowhere to live.
 *
 * The design's sidebar labels the user "Premium User"; this says "Standard
 * user", which is what was asked for. "Premium" implied something bought — a
 * plan you were on, and could therefore fail to be on — where the product is
 * credits-based end to end and has no tiers at all.
 *
 * Worth being honest that "Standard" is still a tier word under a card headed
 * "Plan", so it narrows the problem rather than removing it: it no longer
 * suggests the user is paying for a level, but it can still be read as one
 * level among several. Dropping the row, or naming the model (pay-as-you-go),
 * is the version that would actually settle it. Presentational either way —
 * the real number is the balance beside it.
 */
export default function AccountPage() {
  const { user } = useCurrentUser();
  const logoutAndGoHome = useLogoutRedirect();
  const { balance, loading } = useWallet();

  if (!user) return null;

  const initials = initialsOf(user.displayName);

  const level = balance ? balanceLevel(balance.balanceCredits) : 'ok';

  return (
    <section className="account">
      <h1 className="account__title">Account</h1>

      <Card className="account__identity">
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="account__avatar" />
        ) : (
          <span className="account__avatar account__avatar--initials" aria-hidden>
            {initials}
          </span>
        )}
        <div className="account__identity-text">
          <h2 className="account__name">{user.displayName}</h2>
          <p className="account__email">{user.email}</p>
        </div>
        <Button onClick={logoutAndGoHome}>
          Log out
        </Button>
      </Card>

      <div className="account__grid">
        <Card>
          <CardHeader
            title="Credits"
            description="Credits are spent on video generation. $1 buys 1 credit."
          />
          <div className="account__balance">
            {loading ? (
              <span className="account__balance-value">—</span>
            ) : (
              <>
                <span className="account__balance-value">
                  {balance ? formatCredits(balance.balanceCredits) : '—'}
                </span>
                {balance && balance.onHoldCredits > 0 ? (
                  <span className="account__hold">
                    {formatCredits(balance.onHoldCredits)} on hold for videos in flight
                  </span>
                ) : null}
              </>
            )}
          </div>

          {level !== 'ok' ? (
            <Pill tone={level === 'empty' ? 'danger' : 'warn'} dot>
              {level === 'empty' ? 'Out of credits' : 'Running low'}
            </Pill>
          ) : null}

          <div className="account__actions">
            <ButtonLink to="/wallet" variant="primary">
              Manage wallet
            </ButtonLink>
          </div>
        </Card>

        <Card>
          <CardHeader title="Plan" description="Your current access level." />
          <p className="account__plan">Standard user</p>
          <p className="account__plan-note">
            Generation is billed per video from your credit balance.
          </p>
        </Card>
      </div>
    </section>
  );
}
